import { NextSeo } from 'next-seo'
import BuilderPromo from '../components/BuilderPromo';
import axios from 'axios'
import { wrapper } from '../store/configStore';
import { SERVER_IP } from '../store/middleware/api';
import ClientReviews from '../components/ClientReviews';
import PastEvents from '../components/PastEvents';
import BusinessInfo from '../components/BusinessInfo'
import LandingCategories from '../components/LandingCategories';
import HomepageMainHero from '../components/HomepageMainHero'
import HomepageClosingActionBand from '../components/HomepageClosingActionBand'
import HomepageCorporateReadiness from '../components/HomepageCorporateReadiness'
import UiBoundary from '../components/design-system/foundations/UiBoundary'
import FloridaServiceBanner from '../components/design-system/marketing/FloridaServiceBanner'
import { homepageFallback } from '../content/seo/homepage'
import HomepagePathChoice from '../components/HomepagePathChoice'
import HomepageTrustProof from '../components/HomepageTrustProof'
import FAQGroup from '../components/design-system/marketing/FAQGroup'
import ManagedServiceOverview from '../components/design-system/marketing/ManagedServiceOverview'
import MarketCoverageNotice from '../components/design-system/marketing/MarketCoverageNotice'
import performanceStyles from '../styles/HomepagePerformance.module.scss'

const compactHomepageSeoData = (data, source) => {
  const page = data?.page || homepageFallback.page
  const revision = data?.revision || homepageFallback.revision
  const content = revision.content || {}
  const fallbackSeo = homepageFallback.revision.seo
  const seo = revision.seo || {}

  return {
    page: {
      canonicalUrl: page.canonicalUrl || homepageFallback.page.canonicalUrl || null,
      path: page.path || homepageFallback.page.path,
    },
    revision: {
      content: {
        faqs: Array.isArray(content.faqs) ? content.faqs : [],
        h1: content.h1 || homepageFallback.revision.content.h1,
        hero: {
          ...homepageFallback.revision.content.hero,
          ...(content.hero || {}),
        },
        sections: Array.isArray(content.sections) ? content.sections : [],
      },
      seo: {
        canonicalUrl: seo.canonicalUrl || fallbackSeo.canonicalUrl || null,
        description: seo.description || fallbackSeo.description || null,
        openGraph: {
          ...(fallbackSeo.openGraph || {}),
          ...(seo.openGraph || {}),
        },
        robots: {
          ...(fallbackSeo.robots || {}),
          ...(seo.robots || {}),
        },
        title: seo.title || fallbackSeo.title || null,
        twitter: {
          ...(fallbackSeo.twitter || {}),
          ...(seo.twitter || {}),
        },
      },
    },
    source,
  }
}

const getHomepageSeoData = async ({ preview, previewData }) => {
  try {
    const response = await axios.get(`${SERVER_IP}/seo/pages/${preview ? 'preview' : 'published'}`, {
      params: preview
        ? {
            path: homepageFallback.page.path,
            token: previewData?.seoPreviewToken
          }
        : { path: homepageFallback.page.path }
    })

    return compactHomepageSeoData(
      response.data.data,
      preview ? 'draft-preview' : 'published'
    )
  } catch (error) {
    console.warn(`Using source fallback for ${homepageFallback.page.path}: ${error.message}`)
    return compactHomepageSeoData(homepageFallback, 'source-fallback')
  }
}

export const getStaticProps = wrapper.getStaticProps(
  async ({ store, preview, previewData }) => {
    const [corp, reviewResponse, seoData] = await Promise.all([
      axios.request(`${SERVER_IP}/user/static-landing`),
      axios.get(`${SERVER_IP}/google-review/public`, {
        params: {
          page: '/',
          limit: 3
        }
      }).catch(error => {
        console.warn(`Using review fallback for /: ${error.message}`)
        return { data: null }
      }),
      getHomepageSeoData({ preview, previewData })
    ])

    let { newsletterevents, total_events } = corp?.data
    const homepageEvents = [...(newsletterevents || [])]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3)
      .map(event => ({
        _id: event._id || null,
        category: event.category || null,
        date: event.date || null,
        location: event.location?.name ? { name: event.location.name } : null,
        main_paragraph: event.main_paragraph || null,
        pictures: event.pictures?.[0] ? [event.pictures[0]] : [],
        second_paragraph: event.second_paragraph || null,
        subtitle: event.subtitle || null,
        tags: event.tags || [],
        title: event.title || null,
        venue: event.venue || null,
      }))
    return {
      props: {
        newsletterevents: homepageEvents,
        total_events,
        reviewData: reviewResponse.data,
        seoData,
        isPreview: Boolean(preview)
      },
      revalidate: 3600
    }
  }
);


export default function Home({ newsletterevents, total_events, reviewData, seoData }) {

  const page = seoData?.page || homepageFallback.page
  const revision = seoData?.revision || homepageFallback.revision
  const seo = revision.seo || homepageFallback.revision.seo
  const content = revision.content || homepageFallback.revision.content
  const heroContent = {
    ...homepageFallback.revision.content.hero,
    ...(content.hero || {}),
    title: content.hero?.title || content.h1 || homepageFallback.revision.content.h1
  }
  const pathChoiceContent = (content.sections || []).find(section => section.key === 'homepage-path-choice')
    || homepageFallback.revision.content.sections.find(section => section.key === 'homepage-path-choice')
  const marketCoverageContent = (content.sections || []).find(section => section.key === 'homepage-market-coverage')
    || homepageFallback.revision.content.sections.find(section => section.key === 'homepage-market-coverage')
  const managedServicesContent = (content.sections || []).find(section => section.key === 'homepage-managed-services')
    || homepageFallback.revision.content.sections.find(section => section.key === 'homepage-managed-services')
  const tradeShowManagedService = homepageFallback.revision.content.sections
    .find(section => section.key === 'homepage-managed-services')
    ?.items?.find(item => item.key === 'trade-show')
  const rentalCategoriesContent = (content.sections || []).find(section => section.key === 'homepage-rental-categories')
    || homepageFallback.revision.content.sections.find(section => section.key === 'homepage-rental-categories')
  const reviewsContent = (content.sections || []).find(section => section.key === 'homepage-reviews')
    || homepageFallback.revision.content.sections.find(section => section.key === 'homepage-reviews')
  const corporateReadinessContent = (content.sections || []).find(section => section.key === 'homepage-corporate-readiness')
    || homepageFallback.revision.content.sections.find(section => section.key === 'homepage-corporate-readiness')
  const faqs = content.faqs?.length
    ? content.faqs
    : homepageFallback.revision.content.faqs
  const fallbackExperienceProof = homepageFallback.revision.content.sections
    .find(section => section.key === 'homepage-experience-proof')
  const storedExperienceProof = (content.sections || [])
    .find(section => section.key === 'homepage-experience-proof')
  const experienceProofContent = {
    ...fallbackExperienceProof,
    ...storedExperienceProof,
    settings: {
      ...fallbackExperienceProof?.settings,
      ...storedExperienceProof?.settings,
      projectSpotlight: storedExperienceProof?.settings?.projectSpotlight
        || fallbackExperienceProof?.settings?.projectSpotlight
    }
  }
  const projectSpotlight = experienceProofContent?.settings?.projectSpotlight
  const spotlightPicture = projectSpotlight?.picture
  const openGraph = {
    type: 'website',
    url: seo.openGraph?.url || seo.canonicalUrl || page.canonicalUrl,
    title: seo.title || seo.openGraph?.title,
    description: seo.openGraph?.description || seo.description,
    images: seo.openGraph?.images || [],
    site_name: 'Miami Sound Rental'
  }

  return <>
    <NextSeo
      title={seo.title}
      description={seo.description}
      canonical={seo.canonicalUrl || page.canonicalUrl}
      noindex={seo.robots?.index === false}
      nofollow={seo.robots?.follow === false}
      twitter={{
        handle: '@miamisoundrent1',
        site: '@miamisoundrent1',
        cardType: seo.twitter?.cardType || 'summary_large_image',
      }}
      openGraph={openGraph}
    />

    <UiBoundary className='landing'>
      <HomepageMainHero
        heroContent={heroContent}
        reviewData={reviewData}
        totalEvents={total_events}
      />

      <MarketCoverageNotice content={marketCoverageContent} />

      <HomepagePathChoice content={pathChoiceContent} />
      <div className={performanceStyles.deferredContent}>
        <PastEvents ui='v2' homepage newsletterevents={newsletterevents} total_events={total_events} />
        <ManagedServiceOverview
          additionalItems={tradeShowManagedService ? [tradeShowManagedService] : []}
          content={managedServicesContent}
        />
        <LandingCategories
          ui='v2'
          homepage
          description={rentalCategoriesContent?.body}
          eyebrow={rentalCategoriesContent?.subheading}
          heading={rentalCategoriesContent?.heading}
        />
        <BuilderPromo ui='v2' home />
      {/* {orlando && <section className='questionsBottom'>
                <div className="back"></div>
                <div className='upperBold'>Now Servicing Orlando and Central Florida!</div>
                <p>We have expanded! Our new warehouse in <AudioAction mode='text' href='/orlando'>Orlando</AudioAction> is now open and ready to service all Central Florida! Come visit us!</p>
                <div className="buttons">
                <AudioAction mode='primary' size='large' href='/orlando'>Orlando AV Rentals</AudioAction>

                </div>
            </section>} */}
      {/* <OverBanner picture={ob1} >

        <h4 className='bold'>AV rental made easy</h4>
        <div className='row'>
          <div className='col'>
            <strong className='sub'>Intuitive self-service</strong>
            <p>Find the perfect fit for your need</p>
          </div>
          <div className='col'>
            <strong className='sub'>Simplified online rental</strong>
            <p>Real-time transparent price, rent your equipment in just a few clicks</p>
          </div>
        </div>
        <h4 className='bold'>Plan your event with our easy-to-use digital tools</h4>
        <div className='row'>
          <div className='col'>
            <strong className='sub'>Plan your event setups visually</strong>
            <p>Get exactly what you envision.</p>
          </div>
          <div className='col'>
            <strong className='sub'>Optimize your efforts, ensure results</strong>
            <p>Save time and reduce uncertainty. Trust the best-rated AV partner in South Florida.</p>
          </div>
        </div>

      </OverBanner> */}

      {/* <section className='equipment'>



        <MostRented ui='v2' />
        <div className="otherServices">
          <div className="servCont">
            <h3 className="upperBold">Production Services</h3>
            <div className="serviceRow">
                <div className="service">
                  <PicCont src={corporate} width={600} height={600} />
                  <strong className="serviceTitle bold">Corporate Events</strong>
                  <p>Our top rated service, years of experience in corporate events and our partnerships with top hotels and venues in South Florida makes us the obvious choice when planning your corporate event.</p>
                  <AudioAction mode='primary' href='/corporate-event-production'>Corporate Events</AudioAction>
                </div>
                <div className="service">
                  <PicCont src={live} width={600} height={600} />
                  <strong className="serviceTitle bold">Live Streaming</strong>
                  <p>Broadcast you event live to your audience with our full-service streaming bundles. Conferences, sporting events, religious events and much more.</p>
                  <AudioAction mode='primary' href='/production-services/live-streaming'>Live Streaming</AudioAction>
                </div>
                <div className="service">
                  <PicCont src={tent} width={600} height={600} />
                  <strong className="serviceTitle bold">Party Tents</strong>
                  <p>Rent outdoor party tents for your event in South Florida. Protect your guests from Miami&apos;s sun and rain with our top quality tents.</p>
                  <AudioAction mode='primary' href='/party-tent-rental'>Party Tents</AudioAction>
                </div>

            </div>
          </div>

        </div>
      </section> */}


        <HomepageTrustProof
          content={experienceProofContent}
          image={spotlightPicture
            ? { picture: spotlightPicture }
            : { src: '/Pictures/corporate-event-techs.png' }}
          imageAlt='High-resolution LED video wall for the Pharrell Williams music video at LoanDepot Park'
        />
        <HomepageCorporateReadiness content={corporateReadinessContent} />
        {/* <MostRented ui='v2' /> */}

        <BusinessInfo ui='v2' sectionId='homepage-business-info' />

        <ClientReviews
          ui='v2'
          homepage
          initialData={reviewData}
          heading={reviewsContent?.heading}
          description={reviewsContent?.body}
        />
        <FAQGroup
          questions={faqs}
          eyebrow='Planning answers'
          title='AV rental and production FAQs'
          intro='Answers about service scope, pricing, delivery, production support, and Florida coverage.'
          headingId='homepage-faqs'
        />
        <FloridaServiceBanner />
        <HomepageClosingActionBand />
      </div>
    </UiBoundary>

  </>

}
